import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { api, formatApiErrorDetail } from '../utils/api';
import { Plus, Edit, Trash2, Save, X, ChevronDown, Search } from 'lucide-react';
import { toast } from 'sonner';
import BACKEND_BASE from '../utils/backend';

const API_BASE = BACKEND_BASE;

// Icon preview helper
const IconImg = ({ icon, size = 32, className = '' }) => {
  if (!icon || !icon.image_url) {
    return (
      <div className={`flex items-center justify-center rounded ${className}`}
        style={{ width: size, height: size, backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }}>
        <span style={{ color: '#D1D5DB', fontSize: size * 0.35 }}>?</span>
      </div>
    );
  }
  return (
    <img src={API_BASE + icon.image_url} alt={icon.name}
      className={`object-contain ${className}`}
      style={{ width: size, height: size }}
      onError={(e) => { e.target.style.display = 'none'; }} />
  );
};

// Custom icon dropdown with image previews
const IconSelect = ({ value, onChange, icons, testId }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const selectedIcon = icons.find((i) => i.id === value);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = icons.filter((i) =>
    i.name.toLowerCase().includes(search.toLowerCase()) ||
    (i.category || '').toLowerCase().includes(search.toLowerCase())
  );

  // Group by category
  const grouped = {};
  filtered.forEach((icon) => {
    const cat = icon.category || 'autre';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(icon);
  });

  return (
    <div ref={ref} className="relative" style={{ width: 220 }}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={testId}
        className="w-full flex items-center gap-2 px-3 py-1.5 border rounded text-sm text-left"
        style={{ borderColor: open ? '#1D4ED8' : '#D1D5DB', backgroundColor: '#FFFFFF' }}>
        {selectedIcon ? (
          <>
            <IconImg icon={selectedIcon} size={22} />
            <span className="flex-1 truncate" style={{ color: '#0E2D43' }}>{selectedIcon.name}</span>
          </>
        ) : (
          <span className="flex-1" style={{ color: '#9CA3AF' }}>-- Icone --</span>
        )}
        <ChevronDown size={14} style={{ color: '#6B7280' }} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-72 bg-white border rounded-lg shadow-lg"
          style={{ borderColor: '#E5E7EB', maxHeight: 320, overflowY: 'auto' }}>
          <div className="sticky top-0 bg-white p-2 border-b" style={{ borderColor: '#E5E7EB' }}>
            <div className="flex items-center gap-2 px-2 py-1 border rounded" style={{ borderColor: '#D1D5DB' }}>
              <Search size={14} style={{ color: '#9CA3AF' }} />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher une icone..."
                className="flex-1 text-sm outline-none" style={{ border: 'none' }}
                autoFocus />
            </div>
          </div>

          {/* None option */}
          <button type="button"
            onClick={() => { onChange(null); setOpen(false); setSearch(''); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left"
            style={{ color: '#9CA3AF' }}>
            <div className="w-6 h-6 rounded flex items-center justify-center"
              style={{ backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }}>
              <X size={12} />
            </div>
            Aucune icone
          </button>

          {Object.keys(grouped).length === 0 ? (
            <div className="px-3 py-4 text-center text-sm" style={{ color: '#9CA3AF' }}>
              Aucune icone trouvee
            </div>
          ) : (
            Object.entries(grouped).map(([cat, catIcons]) => (
              <div key={cat}>
                <div className="px-3 py-1 text-xs font-semibold uppercase"
                  style={{ color: '#6B7280', backgroundColor: '#F9FAFB' }}>
                  {cat}
                </div>
                {catIcons.map((icon) => (
                  <button key={icon.id} type="button"
                    onClick={() => { onChange(icon.id); setOpen(false); setSearch(''); }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-blue-50 text-left transition-colors"
                    style={{
                      backgroundColor: icon.id === value ? '#EFF6FF' : 'transparent',
                      color: '#0E2D43',
                    }}>
                    <IconImg icon={icon} size={28} />
                    <span className="flex-1 truncate">{icon.name}</span>
                    {icon.id === value && (
                      <span className="text-xs font-medium px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: '#DBEAFE', color: '#1D4ED8' }}>
                        &#10003;
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const Methods = () => {
  const [methods, setMethods] = useState([]);
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    short_label: '',
    description: '',
    steps: [],
  });

  useEffect(() => {
    Promise.all([loadMethods(), loadIcons()]).finally(() => setLoading(false));
  }, []);

  const loadMethods = async () => {
    try {
      const { data } = await api.get('/methods');
      setMethods(data);
    } catch (error) {
      toast.error('Echec du chargement des methodes');
    }
  };

  const loadIcons = async () => {
    try {
      const { data } = await api.get('/icons');
      setIcons(data);
    } catch (error) {
      toast.error('Echec du chargement des icones');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        short_label: formData.short_label,
        description: formData.description,
        steps: formData.steps.map((s, i) => ({ ...s, order: i })),
      };
      if (editingId) {
        await api.put(`/methods/${editingId}`, payload);
        toast.success('Methode modifiee');
      } else {
        await api.post('/methods', payload);
        toast.success('Methode creee');
      }
      resetForm();
      loadMethods();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleEdit = (method) => {
    const steps = (method.steps || []).sort((a, b) => (a.order || 0) - (b.order || 0));
    setFormData({
      name: method.name,
      short_label: method.short_label || '',
      description: method.description || '',
      steps,
    });
    setEditingId(method.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette methode ?')) return;
    try {
      await api.delete(`/methods/${id}`);
      toast.success('Methode supprimee');
      loadMethods();
    } catch (error) {
      toast.error('Echec de la suppression');
    }
  };

  const addStep = () => {
    setFormData((prev) => ({
      ...prev,
      steps: [...prev.steps, { step_name: '', icon_id: null, order: prev.steps.length }],
    }));
  };

  const removeStep = (index) => {
    setFormData((prev) => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index),
    }));
  };

  const updateStep = (index, field, value) => {
    setFormData((prev) => {
      const steps = [...prev.steps];
      steps[index] = { ...steps[index], [field]: value };
      return { ...prev, steps };
    });
  };

  const moveStep = (index, direction) => {
    setFormData((prev) => {
      const steps = [...prev.steps];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= steps.length) return prev;
      [steps[index], steps[newIndex]] = [steps[newIndex], steps[index]];
      return { ...prev, steps };
    });
  };

  const resetForm = () => {
    setFormData({ name: '', short_label: '', description: '', steps: [] });
    setEditingId(null);
    setShowForm(false);
  };

  const getIconById = (id) => icons.find((i) => i.id === id);

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Chargement...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="methods-page">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold" style={{ color: '#0E2D43', fontFamily: 'Work Sans, sans-serif' }}>
            Methodes de Nettoyage
          </h1>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              data-testid="add-method-button"
              className="flex items-center space-x-2 px-4 py-2 rounded font-medium"
              style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}
            >
              <Plus size={20} />
              <span>Ajouter Methode</span>
            </button>
          )}
        </div>

        {showForm && (
          <div className="bg-white rounded-lg shadow p-6 border mb-8" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: '#0E2D43' }}>
              {editingId ? 'Modifier Methode' : 'Nouvelle Methode'}
            </h2>
            <form onSubmit={handleSubmit} data-testid="method-form">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Nom *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="method-name-input"
                    className="w-full px-4 py-2 border rounded"
                    style={{ borderColor: '#D1D5DB' }}
                    placeholder="ex: Nettoyage surfaces standard"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Label court</label>
                  <input
                    type="text"
                    value={formData.short_label}
                    onChange={(e) => setFormData({ ...formData, short_label: e.target.value })}
                    data-testid="method-label-input"
                    className="w-full px-4 py-2 border rounded"
                    style={{ borderColor: '#D1D5DB' }}
                    placeholder="ex: Surf. Std."
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="method-description-input"
                    className="w-full px-4 py-2 border rounded"
                    style={{ borderColor: '#D1D5DB' }}
                    rows={2}
                  />
                </div>
              </div>

              {/* Steps */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium" style={{ color: '#0E2D43' }}>
                    Etapes de la methode
                  </label>
                  <button
                    type="button"
                    onClick={addStep}
                    data-testid="add-step-button"
                    className="flex items-center space-x-1 px-3 py-1.5 rounded text-sm font-medium"
                    style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8' }}
                  >
                    <Plus size={14} />
                    <span>Ajouter etape</span>
                  </button>
                </div>

                {formData.steps.length === 0 ? (
                  <p className="text-sm py-3 px-4 rounded" style={{ color: '#6B7280', backgroundColor: '#F9FAFB' }}>
                    Aucune etape. Cliquez sur &quot;Ajouter etape&quot; pour definir les etapes de nettoyage.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {formData.steps.map((step, index) => {
                      const stepIcon = step.icon_id ? getIconById(step.icon_id) : null;
                      return (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3 border rounded"
                          style={{ borderColor: '#E5E7EB', backgroundColor: '#FAFAFA' }}
                          data-testid={`step-row-${index}`}
                        >
                          <div className="flex flex-col gap-0.5">
                            <button type="button" onClick={() => moveStep(index, -1)} disabled={index === 0}
                              className="text-xs px-1 rounded disabled:opacity-30" style={{ color: '#0E2D43' }}>
                              &uarr;
                            </button>
                            <button type="button" onClick={() => moveStep(index, 1)} disabled={index === formData.steps.length - 1}
                              className="text-xs px-1 rounded disabled:opacity-30" style={{ color: '#0E2D43' }}>
                              &darr;
                            </button>
                          </div>

                          {/* Step number + icon preview */}
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold w-6 text-center" style={{ color: '#0E2D43' }}>{index + 1}</span>
                            <IconImg icon={stepIcon} size={36} className="rounded" />
                          </div>

                          <input
                            type="text"
                            value={step.step_name}
                            onChange={(e) => updateStep(index, 'step_name', e.target.value)}
                            placeholder="Nom de l'etape (ex: Appliquer)"
                            className="flex-1 px-3 py-1.5 border rounded text-sm"
                            style={{ borderColor: '#D1D5DB' }}
                            data-testid={`step-name-${index}`}
                          />

                          <IconSelect
                            value={step.icon_id}
                            onChange={(val) => updateStep(index, 'icon_id', val)}
                            icons={icons}
                            testId={`step-icon-${index}`}
                          />

                          <button
                            type="button"
                            onClick={() => removeStep(index)}
                            className="px-2 py-1 rounded"
                            style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
                            data-testid={`remove-step-${index}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Live method preview */}
              {formData.steps.length > 0 && (
                <div className="mb-6 p-4 rounded-lg border" data-testid="method-preview"
                  style={{ borderColor: '#D1D5DB', backgroundColor: '#F8FAFC' }}>
                  <p className="text-xs font-semibold uppercase mb-3" style={{ color: '#6B7280', letterSpacing: '0.05em' }}>
                    Apercu de la methode
                  </p>
                  <div className="flex items-start gap-1 flex-wrap justify-center">
                    {formData.steps.map((step, i) => {
                      const icon = step.icon_id ? getIconById(step.icon_id) : null;
                      return (
                        <React.Fragment key={i}>
                          {i > 0 && (
                            <div className="flex items-center pt-3" style={{ color: '#9CA3AF' }}>
                              <span className="text-lg">&rarr;</span>
                            </div>
                          )}
                          <div className="flex flex-col items-center" style={{ width: 80 }}>
                            <div className="w-12 h-12 flex items-center justify-center rounded-lg mb-1"
                              style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
                              <IconImg icon={icon} size={36} />
                            </div>
                            <span className="text-xs text-center leading-tight"
                              style={{ color: '#374151', maxWidth: 76, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                              {step.step_name || '...'}
                            </span>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex space-x-3">
                <button type="submit" data-testid="save-method-button"
                  className="flex items-center space-x-2 px-4 py-2 rounded font-medium"
                  style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
                  <Save size={18} />
                  <span>Enregistrer</span>
                </button>
                <button type="button" onClick={resetForm}
                  className="flex items-center space-x-2 px-4 py-2 rounded border"
                  style={{ borderColor: '#D1D5DB', color: '#0E2D43' }}>
                  <X size={18} />
                  <span>Annuler</span>
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden border" style={{ borderColor: '#E5E7EB' }}>
          <table className="w-full">
            <thead style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium">Nom</th>
                <th className="px-6 py-3 text-left text-sm font-medium">Apercu</th>
                <th className="px-6 py-3 text-left text-sm font-medium">Description</th>
                <th className="px-6 py-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {methods.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center" style={{ color: '#6B7280' }}>
                    Aucune methode. Ajoutez votre premiere methode !
                  </td>
                </tr>
              ) : (
                methods.map((method) => {
                  const steps = (method.steps || []).sort((a, b) => (a.order || 0) - (b.order || 0));
                  return (
                    <tr key={method.id} data-testid={`method-row-${method.id}`} className="border-t" style={{ borderColor: '#E5E7EB' }}>
                      <td className="px-6 py-4">
                        <div>
                          <span className="font-medium" style={{ color: '#0E2D43' }}>{method.name}</span>
                          <span className="text-xs ml-2 px-2 py-0.5 rounded"
                            style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>
                            {steps.length} etape{steps.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-1 flex-wrap">
                          {steps.length > 0 ? steps.map((step, i) => {
                            const icon = step.icon_id ? getIconById(step.icon_id) : null;
                            return (
                              <div key={i} className="flex items-center gap-0.5">
                                {i > 0 && <span className="text-xs" style={{ color: '#D1D5DB' }}>&rarr;</span>}
                                <div className="flex flex-col items-center px-1" title={step.step_name}>
                                  <IconImg icon={icon} size={24} className="rounded" />
                                  <span className="text-xs leading-tight mt-0.5 max-w-16 truncate"
                                    style={{ color: '#6B7280', fontSize: 9 }}>{step.step_name}</span>
                                </div>
                              </div>
                            );
                          }) : (
                            <span className="text-xs" style={{ color: '#9CA3AF' }}>Aucune etape</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm" style={{ color: '#6B7280' }}>
                        {method.description ? (
                          <span className="line-clamp-2">{method.description}</span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button onClick={() => handleEdit(method)} data-testid={`edit-method-${method.id}`}
                          className="inline-flex items-center px-3 py-1 rounded mr-2"
                          style={{ backgroundColor: '#F3F4F6', color: '#0E2D43' }}>
                          <Edit size={16} />
                        </button>
                        <button onClick={() => handleDelete(method.id)} data-testid={`delete-method-${method.id}`}
                          className="inline-flex items-center px-3 py-1 rounded"
                          style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

export default Methods;
